// Tool selection and tool badges of the agents pages (create modal on
// /my-ai-assistants, the « Outils » view of /ai-assistants). Tools come from
// GET /api/tools with their registry metadata (effect, policy, source,
// registered, deprecated_by); they are grouped by what they do.
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Search } from 'lucide-react';
import { Badge, EmptyState } from '../design/ui';
import {
  AGENT_CARRIER_TOOL_RE, groupTools, toolDescription, toolFunctionName, toolGroupMeta, toolLabel,
} from './aiAgentsShared';
import './AIAssistantTools.css';

// What the tool does to the world, and whether it runs at once. hidePolicy:
// the policy is shown by an editor next to the badges (agent « Outils » view).
export const ToolBadges = ({ tool, mode, hidePolicy = false }) => {
  const { t } = useTranslation();
  if (!tool) return null;
  const effects = {
    read: t('tools.effect.read', 'Lecture'),
    propose: t('tools.effect.propose', 'Proposition'),
    commit: t('tools.effect.commit', 'Action'),
  };
  const policy = mode || tool.policy;
  return (
    <>
      {tool.effect && (
        <Badge tone={tool.effect === 'commit' ? 'accent' : tool.effect === 'propose' ? 'info' : undefined} title={[tool.source, tool.cost].filter(Boolean).join(' · ')}>
          {effects[tool.effect] || tool.effect}
        </Badge>
      )}
      {!hidePolicy && (policy === 'proposal' || policy === 'human') && (
        <Badge tone="warning" dot>{t('tools.policyMode.human', 'validation humaine')}</Badge>
      )}
      {!hidePolicy && policy === 'forbidden' && <Badge tone="danger" dot>{t('tools.policyMode.forbidden', 'interdit')}</Badge>}
      {tool.registered === false && (
        <Badge tone="danger" title={t('tools.unregisteredHint', 'Cet outil n’a pas d’exécuteur : il n’est pas proposé à l’assistant.')}>
          {t('tools.unregistered', 'non exécutable')}
        </Badge>
      )}
      {tool.deprecated_by && (
        <Badge title={t('tools.deprecatedBy', 'Remplacé par {{name}}', { name: tool.deprecated_by })}>{t('tools.deprecated', 'historique')}</Badge>
      )}
    </>
  );
};

// The grouped checklist: search, groups with « Tout / Aucun », a line of
// description per tool. selectedIds / onChange(nextIds).
const ToolChecklist = ({ tools, selectedIds, onChange, disabled, compact }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [openGroups, setOpenGroups] = useState({});
  const needle = query.trim().toLowerCase();

  const matching = useMemo(() => (tools || []).filter((tool) => {
    if (!needle) return true;
    const name = toolFunctionName(tool);
    return [name, tool.name, toolLabel(t, name), toolDescription(tool)]
      .some((text) => String(text || '').toLowerCase().includes(needle));
  }), [tools, needle, t]);
  const groups = useMemo(() => groupTools(matching), [matching]);

  const selected = new Set(selectedIds || []);
  const selectedCount = (tools || []).filter((tool) => selected.has(tool.id)).length;
  const agentSetActive = (tools || []).some((tool) => selected.has(tool.id) && AGENT_CARRIER_TOOL_RE.test(toolFunctionName(tool)));

  const isOpen = (group) => (group === 'legacy' ? Boolean(openGroups[group]) || Boolean(needle) : openGroups[group] !== false);
  const toggleGroup = (group) => setOpenGroups((previous) => ({ ...previous, [group]: !isOpen(group) }));

  const setOne = (id, checked) => {
    const next = new Set(selected);
    if (checked) next.add(id); else next.delete(id);
    onChange(Array.from(next));
  };
  const setGroup = (groupTools_, checked) => {
    const next = new Set(selected);
    groupTools_.forEach((tool) => (checked ? next.add(tool.id) : next.delete(tool.id)));
    onChange(Array.from(next));
  };

  if (!(tools || []).length) {
    return <p className="u-help">{t('aiAgentsUi.tools.noneAvailable', 'Aucun outil disponible sur ce compte.')}</p>;
  }

  return (
    <div className={`agent-tools${compact ? ' agent-tools--compact' : ''}`}>
      <div className="agent-tools__bar">
        <div className="u-input-group u-input-group--lead agent-tools__search">
          <Search className="u-input-group__icon" aria-hidden="true" />
          <input
            type="search"
            className="u-input"
            placeholder={t('tools.searchPlaceholder', 'Rechercher un outil (nom ou description)…')}
            aria-label={t('tools.searchPlaceholder', 'Rechercher un outil (nom ou description)…')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <span className="agent-tools__count u-num">{t('aiAgentsUi.tools.selectedCount', '{{count}} sélectionnés', { count: selectedCount })}</span>
      </div>
      {agentSetActive && (
        <p className="u-help agent-tools__notice">
          {t('tools.agentSetActive', 'Cet assistant est sur le jeu d’outils « l’agent choisit le canal » : contact_carrier et les anciens outils de conversation transporteur ne lui sont plus présentés.')}
        </p>
      )}
      <div className="agent-tools__groups">
        {groups.map(({ group, tools: groupList }) => {
          const meta = toolGroupMeta(t, group);
          const open = isOpen(group);
          const inGroup = groupList.filter((tool) => selected.has(tool.id)).length;
          return (
            <section key={group} className="agent-tools__group">
              <div className="agent-tools__group-head">
                <button type="button" className="agent-tools__group-toggle" onClick={() => toggleGroup(group)} aria-expanded={open}>
                  <ChevronRight className={`agent-tools__caret${open ? ' is-open' : ''}`} aria-hidden="true" />
                  <span className="agent-tools__group-title">{meta.title}</span>
                  <span className="agent-tools__group-count u-num">{inGroup}/{groupList.length}</span>
                </button>
                <span className="agent-tools__group-actions">
                  <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setGroup(groupList, true)} disabled={disabled}>{t('tools.selectAll', 'Tout')}</button>
                  <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setGroup(groupList, false)} disabled={disabled}>{t('tools.selectNone', 'Aucun')}</button>
                </span>
              </div>
              {open && meta.hint && <p className="u-help agent-tools__hint">{meta.hint}</p>}
              {open && (
                <ul className="agent-tools__list">
                  {groupList.map((tool) => {
                    const name = toolFunctionName(tool);
                    const label = toolLabel(t, name);
                    const description = toolDescription(tool);
                    const checked = selected.has(tool.id);
                    const inputId = `agent-tool-${compact ? 'new-' : ''}${tool.id}-${name}`;
                    return (
                      <li key={`${tool.id}-${name}`}>
                        <label className={`agent-tools__item${checked ? ' is-selected' : ''}`} htmlFor={inputId}>
                          <input id={inputId} type="checkbox" checked={checked} onChange={(e) => setOne(tool.id, e.target.checked)} disabled={disabled} />
                          <span className="agent-tools__item-body">
                            <span className="agent-tools__item-title">
                              <span className="agent-tools__item-label">{label}</span>
                              {label !== name && <code className="u-code">{name}</code>}
                              <ToolBadges tool={tool} />
                            </span>
                            {description && <span className="agent-tools__item-desc" title={description}>{description}</span>}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
        {!groups.length && (
          <EmptyState icon={Search} title={t('tools.noMatch', 'Aucun outil ne correspond à cette recherche.')} />
        )}
      </div>
    </div>
  );
};

export default ToolChecklist;
